import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { cn } from "../lib/utils"
import { useSidebar } from "../hooks/use-layout"
import { ScrollArea } from "../components/ui/scroll-area"
import { publicAssetPath } from "helpers/publicAssetPath"
import { getAppVersionLabel, getAppVersionShortLabel } from "helpers/appVersion"
import { collectSidebarLinks, resolveActiveNavLink } from "helpers/navActiveLink"
import { resolveSidebarIcon } from "helpers/resolveSidebarIcon"
import {
 Tooltip,
 TooltipContent,
 TooltipProvider,
 TooltipTrigger,
} from "../components/ui/tooltip"
import {
 ChevronLeft,
} from "lucide-react"

interface NavItem {
 label: string
 id?: string
 icon?: string
 link?: string
 subItems?: NavItem[]
 perm?: string | string[]
 type?:"section"
 preload?: () => void
}

interface SidebarProps {
 menuItems: NavItem[]
 logo?: string
 appName?: string
 collapsed?: boolean
 inDrawer?: boolean
}

export function Sidebar({ menuItems, appName, inDrawer }: SidebarProps) {
 const { i18n } = useTranslation()
 const isRtl = i18n.dir() === "rtl"
 const { isCollapsed, closeMobile } = useSidebar()
 const effectiveCollapsed = inDrawer ? false : isCollapsed
 const location = useLocation()
 const fallbackLogo = publicAssetPath("logo.png")
 const [fallbackLogoFailed, setFallbackLogoFailed] = useState(false)
 const showLogo = Boolean(fallbackLogo) && !fallbackLogoFailed

 const allNavLinks = useMemo(() => collectSidebarLinks(menuItems), [menuItems])
 const activeNavLink = useMemo(
  () => resolveActiveNavLink(allNavLinks, location.pathname, location.search),
  [allNavLinks, location.pathname, location.search],
 )
 const appVersionLabel = getAppVersionLabel()
 const appVersionShortLabel = getAppVersionShortLabel()

 const isActive = useCallback(
 (link: string) => activeNavLink === link,
 [activeNavLink],
 )

 const hasActiveChild = useCallback((item: NavItem): boolean => {
 if (!item.subItems || !activeNavLink) return false
 return item.subItems.some((sub) => sub.link === activeNavLink)
 }, [activeNavLink])

 const getMenuKey = useCallback((item: NavItem, index: number) => {
 if (item.type === "section") return `section:${item.label}:${index}`
 return item.id || item.link || `item:${item.label}:${index}`
 }, [])

 const getDefaultOpenMenus = useCallback((): Record<string, boolean> => {
 const open: Record<string, boolean> = {}
 menuItems.forEach((item, index) => {
 if (item.subItems && hasActiveChild(item)) {
 open[getMenuKey(item, index)] = true
 }
 })
 return open
 }, [getMenuKey, hasActiveChild, menuItems])

 const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => getDefaultOpenMenus())

 useEffect(() => {
 setOpenMenus((prev) => {
 const next = { ...prev }
 let changed = false

 menuItems.forEach((item, index) => {
 if (item.subItems && hasActiveChild(item)) {
 const key = getMenuKey(item, index)
 if (!next[key]) {
 next[key] = true
 changed = true
 }
 }
 })

 return changed ? next : prev
 })
 }, [getMenuKey, hasActiveChild, menuItems])

 const toggleMenu = (id: string) => {
 setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }))
 }

 const prevPathnameRef = useRef(location.pathname)
 useEffect(() => {
 if (prevPathnameRef.current !== location.pathname) {
 prevPathnameRef.current = location.pathname
 closeMobile()
 }
 }, [location.pathname, closeMobile])

 useEffect(() => {
 setFallbackLogoFailed(false)
 }, [fallbackLogo])

 const sidebarDir = isRtl ? "rtl" : "ltr"

 return (
 <TooltipProvider delayDuration={0}>
 <aside
 dir={sidebarDir}
 className={cn(
 inDrawer
 ? "app-sidebar relative h-full w-full gradient-sidebar-main text-sidebar-foreground": cn(
"app-sidebar fixed top-[var(--app-banner-h,0px)] z-40 h-[calc(100vh-var(--app-banner-h,0px))] border-e border-sidebar-border/70 gradient-sidebar-main text-sidebar-foreground transition-[width,top,height] duration-200 ease-out",
"hidden md:block",
 isRtl ? "right-0":"left-0",
 isCollapsed ? "w-sidebar-collapsed":"w-sidebar")
 )}
 >
 <div className="flex h-full min-h-0 flex-col">
 {/* ── Logo area ── */}
 <div
 className={cn(
"flex h-14 shrink-0 items-center border-b border-white/[0.06] px-3",
 effectiveCollapsed &&"justify-center px-2")}
 >
 <Link
 to="/" className={cn(
"flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors",
"hover:bg-white/[0.08] hover:text-white",
"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
 effectiveCollapsed &&"justify-center p-1.5")}
 >
 {showLogo ? (
 <img
 src={fallbackLogo}
 alt="Logo" className={cn(
"object-contain brightness-0 invert transition-all duration-300",
" drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]",
 effectiveCollapsed ? "h-9 w-9 rounded-lg":"h-10 w-auto max-w-[148px]")}
 onError={() => setFallbackLogoFailed(true)}
 />
 ) : (
 <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white font-bold text-lg shadow-lg shadow-primary/30">
 E
 </div>
 )}
 {!effectiveCollapsed && (
 <span className="min-w-0 truncate text-[0.9375rem] font-bold leading-tight tracking-tight text-sidebar-primary">
 {appName || "Madar Pos"}
 </span>
 )}
 </Link>
 </div>

 {/* ── Nav ── */}
 <ScrollArea className="min-h-0 flex-1 py-2">
 <nav dir={sidebarDir} className="flex flex-col gap-1 px-2 pb-3">
 {menuItems.map((item, index) => {
 const menuKey = getMenuKey(item, index)
 return (
 <SidebarItem
 key={menuKey}
 item={item}
 isCollapsed={effectiveCollapsed}
 isRtl={isRtl}
 isActive={item.link ? isActive(item.link) : hasActiveChild(item)}
 activeNavLink={activeNavLink}
 isOpen={openMenus[menuKey] || false}
 onToggle={() => toggleMenu(menuKey)}
 />
 )
 })}
 </nav>
 </ScrollArea>

 {/* ── Footer ── */}
 <div
 className={cn(
"border-t border-white/10 px-3 py-2.5",
 effectiveCollapsed &&"flex justify-center px-2 py-2")}
 >
 {!effectiveCollapsed ? (
 <div className="flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5">
 <span className="h-1 w-1 shrink-0 rounded-full bg-white/35"aria-hidden />
     <span className="text-[11px] font-medium text-white/50 tabular-nums">
      {appVersionLabel}
     </span>
 </div>
 ) : (
 <Tooltip>
 <TooltipTrigger asChild>
 <div
      className="flex h-8 w-8 cursor-default items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-[10px] font-semibold text-white/50 tabular-nums" aria-label={appVersionLabel}>
      {appVersionShortLabel}
 </div>
 </TooltipTrigger>
     <TooltipContent side={isRtl ? "left" : "right"} className="font-medium">
      {appVersionLabel}
     </TooltipContent>
 </Tooltip>
 )}
 </div>
 </div>
 </aside>
 </TooltipProvider>
 )
}

interface SidebarItemProps {
 item: NavItem
 isCollapsed: boolean
 isRtl: boolean
 isActive: boolean
 activeNavLink: string | null
 isOpen: boolean
 onToggle: () => void
}

function SidebarItem({ item, isCollapsed, isRtl, isActive, activeNavLink, isOpen, onToggle }: SidebarItemProps) {
 const IconComponent = item.icon ? resolveSidebarIcon(item.icon) : null

 const isSubActive = (link: string) => activeNavLink === link

 if (item.type === "section") {
 return isCollapsed ? null : (
 <div className="px-1 pt-4 pb-1 first:pt-2">
 <div className="flex items-center gap-2 px-2">
 <span className="h-px min-w-[1rem] flex-1 bg-white/10" aria-hidden />
 <span className="shrink-0 text-[11px] font-semibold leading-none text-white/55">
 {item.label}
 </span>
 <span className="h-px min-w-[1rem] flex-1 bg-white/10" aria-hidden />
 </div>
 </div>
 )
 }

 if (item.subItems) {
 return (
 <div>
 <SidebarItemWrapper isCollapsed={isCollapsed} isRtl={isRtl} tooltip={item.label}>
 <button
 type="button"
onClick={onToggle}
 className={cn(
"flex w-full min-h-[2.5rem] items-center gap-3 rounded-xl border-s-2 border-s-transparent px-3 py-2 text-sm font-medium transition-colors duration-200",
"hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground",
 isActive
 ? "border-s-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)_/_0.14)] font-semibold text-sidebar-accent-foreground": isOpen
 ? "bg-sidebar-accent/45 text-sidebar-foreground":"text-sidebar-foreground/85")}
 >
 {IconComponent && (
 <IconComponent
 className={cn(
"h-5 w-5 shrink-0 transition-colors",
 isActive
 ? "text-[hsl(var(--brand-accent))]":"text-sidebar-foreground/50")}
 />
 )}
 {!isCollapsed && (
 <>
 <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
 <ChevronLeft
 className={cn(
"h-4 w-4 shrink-0 text-sidebar-foreground/45 transition-transform duration-200",
 isRtl && !isOpen && "rotate-180",
 isOpen && (isRtl ? "rotate-90" : "-rotate-90"))}
 />
 </>
 )}
 </button>
 </SidebarItemWrapper>
 {!isCollapsed && isOpen && (
 <div className="ms-2 mt-1 space-y-0.5 rounded-xl bg-black/15 py-1.5 ps-2 pe-1">
 {item.subItems.map((sub, idx) => {
  if (sub.type === 'section') {
   return (
    <div
     key={`section-${sub.label}-${idx}`}
     className="px-2.5 pt-2 pb-0.5 text-[11px] font-semibold leading-snug text-sidebar-foreground/50 first:pt-1"
    >
     {sub.label}
    </div>
   )
  }
  return (
 <Link
 key={sub.link || idx}
 to={sub.link || "/"}
 className={cn(
"flex min-h-[2.25rem] items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] leading-snug transition-colors duration-200",
 sub.link && isSubActive(sub.link)
 ? "bg-[hsl(var(--brand-accent)_/_0.16)] font-semibold text-white":"text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground")}
 >
 <span
 className={cn(
"me-0.5 h-1.5 w-1.5 shrink-0 rounded-full transition-colors",
 sub.link && isSubActive(sub.link)
 ? "bg-[hsl(var(--brand-accent))]":"bg-sidebar-foreground/25")}
 />
 <span className="min-w-0 flex-1 truncate">{sub.label}</span>
 </Link>
  )
 })}
 </div>
 )}
 </div>
 )
 }

 return (
 <SidebarItemWrapper isCollapsed={isCollapsed} isRtl={isRtl} tooltip={item.label}>
 <Link
 to={item.link || "/"}
 onMouseEnter={() => item.preload?.()}
 className={cn(
"flex min-h-[2.5rem] items-center gap-3 rounded-xl border-s-2 border-s-transparent px-3 py-2 text-sm font-medium transition-colors duration-200",
 isActive
 ? "border-s-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)_/_0.14)] font-semibold text-sidebar-accent-foreground":"text-sidebar-foreground/85 hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground")}
 >
 {IconComponent && (
 <IconComponent
 className={cn(
"h-5 w-5 shrink-0 transition-colors",
 isActive
 ? "text-[hsl(var(--brand-accent))]":"text-sidebar-foreground/50")}
 />
 )}
 {!isCollapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
 </Link>
 </SidebarItemWrapper>
 )
}

function SidebarItemWrapper({
 isCollapsed,
 isRtl,
 tooltip,
 children,
}: {
 isCollapsed: boolean
 isRtl: boolean
 tooltip: string
 children: React.ReactNode
}) {
 if (isCollapsed) {
 return (
 <Tooltip>
 <TooltipTrigger asChild>{children}</TooltipTrigger>
 <TooltipContent side={isRtl ? "left" : "right"} className="max-w-[220px] font-medium">
 {tooltip}
 </TooltipContent>
 </Tooltip>
 )
 }
 return <>{children}</>
}
